/**
 * @file useGoogleAuth.ts
 * @description React hook for Google OAuth state management
 */

import { useState, useEffect, useCallback } from 'react';
import { getStoredToken } from '../contexts/AuthContext';

interface GoogleAuthState {
  isConfigured: boolean;
  isAuthenticated: boolean;
  connectedServices: ('gmail' | 'calendar' | 'nest')[];
  loading: boolean;
  error: string | null;
  expiresAt: number | null;
}

interface UseGoogleAuthReturn extends GoogleAuthState {
  connect: (services?: ('gmail' | 'calendar' | 'nest')[]) => void;
  disconnect: () => Promise<void>;
  refresh: () => Promise<void>;
}

export function useGoogleAuth(): UseGoogleAuthReturn {
  const [state, setState] = useState<GoogleAuthState>({
    isConfigured: false,
    isAuthenticated: false,
    connectedServices: [],
    loading: true,
    error: null,
    expiresAt: null
  });

  // Check for OAuth callback results in URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const googleConnected = urlParams.get('google_connected');
    const googleError = urlParams.get('google_error');

    if (googleConnected === 'true') {
      // Clear URL params and refresh status
      window.history.replaceState({}, '', window.location.pathname);
      fetchStatus();
    } else if (googleError) {
      setState(prev => ({
        ...prev,
        error: decodeURIComponent(googleError),
        loading: false
      }));
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // Fetch current status on mount
  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      setState(prev => ({ ...prev, loading: true, error: null }));

      const token = getStoredToken();
      const response = await fetch('/api/google/status', {
        headers: {
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        }
      });
      const data = await response.json();

      if (data.success) {
        setState({
          isConfigured: data.configured,
          isAuthenticated: data.isAuthenticated,
          connectedServices: data.connectedServices || [],
          loading: false,
          error: null,
          expiresAt: data.expiresAt
        });
      } else {
        setState(prev => ({
          ...prev,
          loading: false,
          error: data.error || 'Failed to get status'
        }));
      }
    } catch (error) {
      setState(prev => ({
        ...prev,
        loading: false,
        error: 'Failed to connect to server'
      }));
    }
  };

  const connect = useCallback((services?: ('gmail' | 'calendar' | 'nest')[]) => {
    const serviceParam = services ? `&services=${services.join(',')}` : '';
    const token = getStoredToken();

    // Request auth URL from server
    fetch(`/api/google/auth-url?userId=default${serviceParam}`, {
      headers: {
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
      }
    })
      .then(res => res.json())
      .then(data => {
        if (data.success && data.authUrl) {
          // Redirect to Google OAuth consent
          window.location.href = data.authUrl;
        } else {
          setState(prev => ({
            ...prev,
            error: data.error || 'Failed to get authorization URL'
          }));
        }
      })
      .catch(error => {
        setState(prev => ({
          ...prev,
          error: 'Failed to initiate connection'
        }));
      });
  }, []);

  const disconnect = useCallback(async () => {
    try {
      setState(prev => ({ ...prev, loading: true }));

      const token = getStoredToken();
      const response = await fetch('/api/google/revoke', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ userId: 'default' })
      });

      const data = await response.json();

      if (data.success) {
        setState({
          isConfigured: true,
          isAuthenticated: false,
          connectedServices: [],
          loading: false,
          error: null,
          expiresAt: null
        });
      } else {
        setState(prev => ({
          ...prev,
          loading: false,
          error: data.error || 'Failed to disconnect'
        }));
      }
    } catch (error) {
      setState(prev => ({
        ...prev,
        loading: false,
        error: 'Failed to disconnect'
      }));
    }
  }, []);

  const refresh = useCallback(async () => {
    await fetchStatus();
  }, []);

  return {
    ...state,
    connect,
    disconnect,
    refresh
  };
}

export default useGoogleAuth;
